import { useReactiveVar } from '@apollo/client'
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { MoreHorizontal } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import { saveAs } from 'file-saver'
import {
  isCostCalculationSuccessVar,
  projectVar,
  selectedCostCalculatorsVar,
} from '../../apollo/reactive-vars'
import { createCostExport } from '../../costs/create-cost-export'
import { pageStyle } from '../common/report-styles'
import { CostsReport } from './costs-report/CostsReport'

// Used only to allow legibility, do not use elsewhere
function timeout(ms: number) {
  // eslint-disable-next-line no-promise-executor-return
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export default function CostsMenu(): React.ReactElement {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const { t } = useTranslation()
  const isCostCalculationSuccess = useReactiveVar(isCostCalculationSuccessVar)
  const versionCostCalculators = useReactiveVar(selectedCostCalculatorsVar)
  const [render, setRender] = useState(false)
  const printableComponentRef = useRef<HTMLDivElement>(null)
  const onBeforeGetContentResolve = useRef<(value: unknown) => void>()

  const handleOnBeforeGetContent = () =>
    new Promise((resolve) => {
      onBeforeGetContentResolve.current = resolve
      setRender(true)
    })

  const handlePrint = useReactToPrint({
    content: () => printableComponentRef.current,
    documentTitle: `${projectVar()?.name ?? ''}_${t('costs.title')}`,
    pageStyle,
    onBeforeGetContent: handleOnBeforeGetContent,
    onAfterPrint: () => setRender(false),
  })

  useEffect(() => {
    async function resolveContent() {
      if (render && onBeforeGetContentResolve.current) {
        // A timeout is necessary for browser to render the canvas elements.
        // The current implementation of react-to-print does not seem
        // to properly wait for the canvas elements in the browser window.
        // 1ms tested to be sufficient locally, 10ms chosen for safety
        await timeout(10)
        onBeforeGetContentResolve.current(true)
      }
    }
    void resolveContent()
  }, [render, onBeforeGetContentResolve])

  if (!isCostCalculationSuccess) {
    return (
      <Box>
        <Tooltip title={String(t('error.energyCalculationMissing'))}>
          <span>
            <IconButton disabled>
              <MoreHorizontal />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    )
  }

  const createExport = async () => {
    const workbook = createCostExport(t, versionCostCalculators)
    const buffer = await workbook.xlsx.writeBuffer()

    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const projectName = projectVar()?.name ?? ''
    const fileName = `${projectName}-cost-export.xlsx`
    saveAs(blob, fileName)
  }

  return (
    <Box>
      <IconButton
        id="open-costs-menu-button"
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        <MoreHorizontal />
      </IconButton>

      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null)
            void createExport()
          }}
        >
          {t('costs.export.title')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null)
            if (handlePrint && printableComponentRef) {
              handlePrint()
            }
          }}
        >
          {t('common.print')}
        </MenuItem>
      </Menu>
      <div style={{ overflow: 'hidden', height: 0, width: 0 }}>
        {render && (
          <CostsReport
            versionCostCalculators={versionCostCalculators}
            ref={printableComponentRef}
          />
        )}
      </div>
    </Box>
  )
}
